/** Authored visual motion only. No weapon/armour performance is calculated here. */
export const CHAPTERS = [0, .22, .50, .69, .88] as const;
export const CONTACT = { x: .18, y: 1.15, z: 1.235 } as const;
export const PROJECTILE_LENGTH = .030;
export const MUZZLE_EXIT_Z = 8.2;
// A shallow surface impression only; the vehicle GLB retains its original editable morph.
export const DENT_DEPTH = .00065;
export const AUTHORED_PANEL_DENT_DEPTH = .008;
export const unit = (n: number) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
const smoother = (n: number) => { const t = unit(n); return t * t * t * (t * (t * 6 - 15) + 10); };
const range = (p: number, a: number, b: number) => smoother((p - a) / (b - a));
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
type Knot = readonly [number, number];

// Monotonic cubic Hermite curve: shared derivatives keep the travel continuous at joins.
function motionCurve(knots: readonly Knot[]) {
  const d = knots.slice(1).map((b, i) => (b[1] - knots[i][1]) / (b[0] - knots[i][0]));
  const slopes = knots.map((_, i) => i === 0 || i === knots.length - 1 || d[i - 1] * d[i] <= 0 ? 0 : 2 / (1 / d[i - 1] + 1 / d[i]));
  return (p: number) => {
    let i = 0; while (i < knots.length - 2 && p > knots[i + 1][0]) i++;
    const [a, av] = knots[i], [b, bv] = knots[i + 1];
    if (av === bv) return av;
    const t = unit((p - a) / (b - a)), t2 = t * t, t3 = t2 * t;
    const value = (2 * t3 - 3 * t2 + 1) * av + (t3 - 2 * t2 + t) * (b - a) * slopes[i]
      + (-2 * t3 + 3 * t2) * bv + (t3 - t2) * (b - a) * slopes[i + 1];
    return Math.min(Math.max(av, bv), Math.max(Math.min(av, bv), value));
  };
}
const travel = motionCurve([[0, 8.206 + PROJECTILE_LENGTH], [.14, 8.10], [.27, 6.6], [.47, 2.15], [.66, CONTACT.z + PROJECTILE_LENGTH], [1, CONTACT.z + PROJECTILE_LENGTH]]);
const logDistance = motionCurve([[0, Math.log(11.2)], [.06, Math.log(9.7)], [.22, Math.log(.72)], [.40, Math.log(.28)], [.57, Math.log(.20)], [.78, Math.log(.16)], [.82, Math.log(.20)], [.88, Math.log(.85)], [.94, Math.log(1.65)], [1, Math.log(2.0)]]);
const azimuth = motionCurve([[0, 35], [.22, 42], [.5, 48], [.70, 29], [.82, 23], [1, 51]]);
const elevation = motionCurve([[0, 16], [.22, 12], [.5, 10], [.82, 8], [1, 17]]);

export function sampleDefenceStory(value: number, aspect = 16 / 9) {
  const p = unit(value), crush = range(p, .69, .81), reveal = range(p, .735, .915);
  const projectileZ = travel(p) - DENT_DEPTH * crush;
  const follow = range(p, .035, .22), panelFocus = range(p, .55, .70);
  const target: [number, number, number] = [CONTACT.x, mix(1.25, CONTACT.y, follow), mix(3.6, projectileZ - PROJECTILE_LENGTH / 2, follow)];
  target[2] = mix(target[2], CONTACT.z - DENT_DEPTH * crush + .015 - .060 * reveal, panelFocus);
  const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 16 / 9;
  const portrait = Math.max(1, .95 / safeAspect);
  const distance = Math.exp(logDistance(p)) * portrait * (safeAspect < .85 ? (1 + .45 * (1 - follow)) * (1 + .40 * reveal) : 1);
  const yaw = azimuth(p) * Math.PI / 180, pitch = elevation(p) * Math.PI / 180;
  const eye: [number, number, number] = [target[0] + distance * Math.cos(pitch) * Math.cos(yaw), target[1] + distance * Math.sin(pitch), target[2] + distance * Math.cos(pitch) * Math.sin(yaw)];
  const weights = CHAPTERS.map((start, i) => (i ? range(p, start, start + .025) : 1) * (i === 4 ? 1 : 1 - range(p, CHAPTERS[i + 1] - .025, CHAPTERS[i + 1])));
  const contactAge = unit((p - .695) / .12);
  return { p, eye, target, distance, projectileZ, crush, panelDent: crush * DENT_DEPTH / AUTHORED_PANEL_DENT_DEPTH, reveal, outerSkinOpacity: 1 - .965 * reveal, weights,
    chapter: CHAPTERS.reduce<number>((current, start, i) => p >= start ? i : current, 0),
    near: Math.max(.001, Math.min(.06, distance / 120)), fov: 42,
    macro: range(p, .18, .39) * (1 - range(p, .86, 1)),
    flash: Math.sin(Math.PI * unit((p - .015) / .09)) * .18,
    contactAge,
    contactGlow: Math.sin(Math.PI * contactAge) * (1 - contactAge),
  };
}
